/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Skeleton,
    Box,
    Text,
    Stack,
    Select,
    Link as LinkPlaceholder,
    Image,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// project components
import Swatch from '../../../components/swatch-group/swatch'
import SwatchGroup from '../../../components/swatch-group'
import {getUrl} from '../../../utils/cloudinary-utils'
import {useIntl} from 'react-intl'

export const QuickAddSwatches = ({
    productVariationAttributes,
    setProduct,
    product,
    setSelectedVariantId,
    variant,
    showOptionsMessage,
    productWithFilteredVariants,
    showSwatches,
    showLoading
}) => {
    const formStyles = useStyleConfig('FormStyles')
    const [hideMoreSwatches, setHideMoreSwatches] = useState(true)
    const intl = useIntl()
    let hasMetal = false
    let swatchGroupName = ''
    return (
        <Stack
            gap="5"
            sx={{
                'button[aria-checked="false"] div[data-id="letter"]': {
                    paddingTop: '2px'
                }
            }}
        >
            {/*
                Customize the skeletons shown for attributes to suit your needs. At the point
                that we show the skeleton we do not know how many variations are selectable. So choose
                a skeleton that will meet most of your needs.
            */}
            {showLoading ? (
                <>
                    {/* First Attribute Skeleton */}
                    <Skeleton height={6} width={32} />
                    <Skeleton height={20} width={64} />

                    {/* Second Attribute Skeleton */}
                    <Skeleton height={6} width={32} />
                    <Skeleton height={20} width={64} />
                </>
            ) : (
                showSwatches &&
                productVariationAttributes
                    ?.filter(({values}) => values?.length)
                    // Filter out swatches that do not correspond to 'any' variant
                    ?.map((attr) => ({
                        ...attr,
                        values: attr?.values?.filter((value) => {
                            const params = Object.fromEntries(
                                new URLSearchParams(value?.href?.split('?')?.[1] || '')
                            )

                            const selectedVariant =
                                productWithFilteredVariants?.variants?.find((variant) =>
                                    Object.entries(params)?.every(
                                        ([key, val]) => variant?.variationValues?.[key] === val
                                    )
                                ) || false
                            return productWithFilteredVariants?.c_allPdpLinks?.some(
                                (p) =>
                                    p.id === selectedVariant?.productId &&
                                    (p?.searchableIfUnavailableFlag || p?.orderable)
                            )
                        })
                    }))
                    ?.filter((attr) => attr.values.length)
                    ?.map(({id, name, selectedValue, values}, index) => {
                        hasMetal =
                            (!hasMetal && id == 'metal') || (hasMetal && id != 'metal')
                                ? true
                                : false
                        swatchGroupName = name
                        const errorStyles = showOptionsMessage
                            ? {
                                  borderColor: 'red.400 !important',
                                  '&:hover': {
                                      borderColor: 'red.400 !important',
                                      boxShadow:
                                          '0px 3px var(--chakra-colors-purple-400) !important'
                                  }
                              }
                            : {
                                  '&:hover': {
                                      borderColor: 'purple.300 !important'
                                  }
                              }
                        if (id === 'carat' || id === 'size') {
                            return (
                                <Select
                                    key={`${id}-${index}`}
                                    value={selectedValue?.value || ''}
                                    {...formStyles.selectField}
                                    sx={errorStyles}
                                    invalid={showOptionsMessage.toString()}
                                    icon={
                                        <Image
                                            alt="Arrow"
                                            src={getUrl('UIImages/images/down-arrow-new.svg')}
                                            {...formStyles.selectIcon}
                                            width="13px"
                                            height="13px"
                                            marginRight="15px"
                                        />
                                    }
                                    onChange={(e) => {
                                        const selectedSize = values.find(
                                            (size) => size.value === e.target.value
                                        )
                                        const {variants = []} = product
                                        const selectedVariationValues = {
                                            ...variant?.variationValues,
                                            [id]: selectedSize.value
                                        }
                                        const selectedVariant =
                                            variants.filter(({variationValues}) =>
                                                // A single liner that will return true if the current variation has all the
                                                // same attribute values as the passing in params.
                                                Object.keys(selectedVariationValues).every(
                                                    (key) =>
                                                        variationValues[key] ===
                                                        selectedVariationValues[key]
                                                )
                                            )?.[0] || {}
                                        setSelectedVariantId(selectedVariant?.productId)
                                        setProduct({
                                            ...product,
                                            ...selectedVariant
                                        })
                                    }}
                                    backgroundColor="white"
                                >
                                    <option value="" disabled>
                                        {id === 'carat' ? (
                                            <FormattedMessage
                                                defaultMessage="Select a carat size"
                                                id="select.a.carat.size"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="Select a size"
                                                id="select.a.size"
                                            />
                                        )}
                                    </option>
                                    {values?.map(({name, value, orderable}) => (
                                        <option key={value} value={value} disabled={!orderable}>
                                            {name}
                                        </option>
                                    ))}
                                </Select>
                            )
                        }
                        const swatches = values?.map(
                            ({href, name, image, value, orderable}, index) => {
                                const content = image ? (
                                    <Box
                                        height="100%"
                                        width="100%"
                                        display={index > 3 && hideMoreSwatches ? 'none' : 'block'}
                                        backgroundRepeat="no-repeat"
                                        backgroundSize="cover"
                                        fontSize="8px"
                                        color="black.100"
                                        data-id={id}
                                        backgroundColor={
                                            id === 'letter' ? 'gray.400' : name.toLowerCase()
                                        }
                                        style={{
                                            backgroundImage:
                                                id === 'letter'
                                                    ? 'unset'
                                                    : `url('${image.disBaseLink || image.link}')`
                                        }} // Wrap-up backgroundImage to fix the issues caused by ",".
                                    >
                                        {id === 'letter' ? name : ''}
                                    </Box>
                                ) : (
                                    name
                                )
                                const hasSelection = Boolean(selectedValue?.value)
                                const isSelected = selectedValue?.value === value
                                const isFirst = index === 0
                                // To mimic the behavior of a native radio input, only
                                // one swatch should receive tab focus; the rest can be
                                // selected using arrow keys when the swatch group has
                                // focus. The focused element is the selected option or
                                // the first in the group, if no option is selected.
                                // This is a slight difference, for simplicity, from the
                                // native element, where the first element is focused on
                                // `Tab` and the _last_ element is focused on `Shift+Tab`
                                const isFocusable = isSelected || (!hasSelection && isFirst)
                                const sizeProps = {}
                                return (
                                    <Swatch
                                        key={`${id}-${value}-${index}`}
                                        href={href}
                                        disabled={!orderable}
                                        value={value}
                                        name={name}
                                        ispdp={false}
                                        isProductPartOfSet={true}
                                        variant={
                                            id === 'size' || id === 'length' ? 'square' : 'circle'
                                        }
                                        selected={isSelected}
                                        swatchProps={{
                                            display:
                                                index > 2 && hideMoreSwatches && id !== 'size'
                                                    ? 'none'
                                                    : 'block',
                                            ...sizeProps
                                        }}
                                        isFocusable={isFocusable}
                                        attributeId={id}
                                        isCustomSwatch
                                        customSwatchSelect={(variationParams) => {
                                            const {variants = []} = product
                                            const selectedVariationValues = {
                                                ...variant?.variationValues,
                                                ...variationParams
                                            }
                                            const selectedVariant =
                                                variants?.filter(({variationValues}) =>
                                                    // A single liner that will return true if the current variation has all the
                                                    // same attribute values as the passing in params.
                                                    Object.keys(selectedVariationValues)?.every(
                                                        (key) =>
                                                            variationValues?.[key] ===
                                                            selectedVariationValues[key]
                                                    )
                                                )?.[0] || {}
                                            setSelectedVariantId(selectedVariant?.productId)
                                            setProduct({
                                                ...product,
                                                ...selectedVariant
                                            })
                                        }}
                                    >
                                        {content}
                                    </Swatch>
                                )
                            }
                        )
                        return (
                            <Box key={id}>
                                {id === 'size' ? (
                                    <Box
                                        fontWeight="400"
                                        fontSize="16px"
                                        color="black.100"
                                        display="inline-flex"
                                    >
                                        {id === 'size' ? 'Size' : ''}
                                        <Box fontWeight="300" marginLeft="15px">
                                            {selectedValue?.name || `Select a ${id}`}
                                        </Box>
                                    </Box>
                                ) : null}

                                <SwatchGroup
                                    groupProps={{}}
                                    value={selectedValue?.value}
                                    displayName={selectedValue?.name || ''}
                                    onClick={(e) => {
                                        e.preventDefault()
                                    }}
                                    handleChange={(e) => {
                                        if (typeof e !== 'string') {
                                            setHideMoreSwatches(false)
                                        }
                                    }}
                                    pdpSwatches={product?.c_PDPSwatches}
                                    label={
                                        hasMetal
                                            ? intl.formatMessage(
                                                  {
                                                      defaultMessage: 'Select a {groupname}',
                                                      id: 'product_view.label.group_variant_type'
                                                  },
                                                  {groupname: swatchGroupName}
                                              )
                                            : ''
                                    }
                                    showLabel={hasMetal}
                                    isRecommendation={true}
                                >
                                    {swatches}
                                    {values?.length > 3 && hideMoreSwatches && id !== 'size' && (
                                        <LinkPlaceholder
                                            onClick={(e) => {
                                                e.preventDefault()
                                                setHideMoreSwatches(false)
                                            }}
                                            alignContent="center"
                                            _hover={{
                                                textDecoration: 'none'
                                            }}
                                            textDecoration="none"
                                        >
                                            <Text
                                                color="black.100"
                                                fontWeight="300"
                                                fontSize="13px"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="+ {count} more"
                                                    values={{
                                                        count: values.length - 3
                                                    }}
                                                    id="product_tile.button.more_swatches"
                                                />
                                            </Text>
                                        </LinkPlaceholder>
                                    )}
                                </SwatchGroup>
                            </Box>
                        )
                    })
            )}
        </Stack>
    )
}

QuickAddSwatches.propTypes = {
    productVariationAttributes: PropTypes.any,
    setProduct: PropTypes.func,
    product: PropTypes.any,
    setSelectedVariantId: PropTypes.any,
    variant: PropTypes.any,
    showOptionsMessage: PropTypes.bool,
    productWithFilteredVariants: PropTypes.any,
    showSwatches: PropTypes.bool,
    showLoading: PropTypes.bool
}
